import { Download, FileText, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import {
  BottomSheet,
  Button,
  Card,
  ListRow,
  ProBadge,
  SectionHeader,
  useToast,
} from '../../components';
import { buildCategoryMap, useActions, useAppData } from '../../data';
import type { Transaction } from '../../data/types';

/** Escape a single CSV cell — wrap in quotes if needed, escape internal quotes. */
function csvCell(value: string | number): string {
  const s = String(value ?? '');
  if (/["\n,]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(
  transactions: Transaction[],
  categoryNameById: Map<string, string>,
): string {
  const header = [
    'Date',
    'Type',
    'Category',
    'Description',
    'Amount',
    'Source',
  ];
  const rows = transactions.map((t) =>
    [
      t.date,
      t.type,
      categoryNameById.get(t.categoryId) ?? '',
      t.description,
      t.amount.toFixed(2),
      t.source,
    ].map(csvCell).join(','),
  );
  return [header.join(','), ...rows].join('\n');
}

function triggerDownload(filename: string, content: string, mime: string) {
  if (typeof document === 'undefined') return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function DataSection() {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();

  const [confirmReset, setConfirmReset] = useState(false);

  const exportCsv = () => {
    const map = buildCategoryMap(data.categories);
    const named = new Map<string, string>();
    for (const [id, c] of map) named.set(id, c.name);
    const csv = buildCsv(data.transactions, named);
    const stamp = new Date().toISOString().slice(0, 10);
    triggerDownload(`margin-${stamp}.csv`, csv, 'text/csv;charset=utf-8;');
    toast.show({
      message: `Exported ${data.transactions.length} transactions`,
      kind: 'success',
    });
  };

  return (
    <div>
      <SectionHeader title="Data" />
      <Card flush elevation="sm" className="mb-section-gap py-1">
        <ListRow
          icon={<Download size={18} strokeWidth={1.5} />}
          title="Export CSV"
          subtitle={`${data.transactions.length} transaction${
            data.transactions.length === 1 ? '' : 's'
          }`}
          accessory="chevron"
          onClick={exportCsv}
        />
        <div className="h-px bg-surface-secondary mx-card-pad" />
        <ListRow
          icon={<FileText size={18} strokeWidth={1.5} />}
          title={
            <span className="inline-flex items-center gap-2">
              Export PDF report <ProBadge />
            </span>
          }
          subtitle="Available with Pro"
          accessory="chevron"
          onClick={() =>
            toast.show({
              message: 'PDF export arrives with Pro',
              kind: 'info',
            })
          }
        />
      </Card>

      <Card elevation="sm" className="mb-section-gap mt-12">
        <Button
          variant="destructive"
          fullWidth
          leadingIcon={<RotateCcw size={18} strokeWidth={1.5} />}
          onClick={() => setConfirmReset(true)}
        >
          Reset all data
        </Button>
        <p className="mt-2 text-[12px] text-text-tertiary text-center">
          Sends you back through onboarding. There is no undo.
        </p>
      </Card>

      <BottomSheet
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset all data?"
      >
        <div className="px-card-pad py-4 flex flex-col gap-3">
          <p className="text-[14px] text-text-secondary text-center">
            Every transaction, category, scheduled item, goal, and preference
            will be wiped. This can’t be undone.
          </p>
          <Button
            variant="destructive"
            size="lg"
            fullWidth
            leadingIcon={<RotateCcw size={18} strokeWidth={1.5} />}
            onClick={() => {
              actions.resetAllData();
              setConfirmReset(false);
              toast.show({ message: 'Reset to defaults', kind: 'info' });
            }}
          >
            Yes, reset everything
          </Button>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={() => setConfirmReset(false)}
          >
            Cancel
          </Button>
        </div>
      </BottomSheet>
    </div>
  );
}
